import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { Auth } from "aws-amplify";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AuthLayout from "../../layouts/AuthLayout";
import { XMarkIcon } from "../../assets/svgs";
import { changePasswordApi } from "../../api";
import { useRequest } from "ahooks";

const Confirm: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const search = useLocation().search;
  const token = new URLSearchParams(search).get("token");
  const otp = new URLSearchParams(search).get("otp");
  const [loading, setLoading] = useState<boolean>(true);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [email, setEmail] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  const confirmAccount = async (email: string, code: string) => {
    try {
      await Auth.confirmSignUp(email, code);
      toast.success(t("accountConfirmed"));
      setConfirmed(true);
      setLoading(false);
    } catch (err: any) {
      if (/Current status is CONFIRMED/.test(err?.message)) {
        toast.success("User is already confirmed");
        history.push("/dashboard/login");
        return;
      }
      toast.error("Link is either expired or incorrect.");
      setConfirmed(false);
      setLoading(false);
      setMessage("Link is either expired or incorrect.");
    }
  };

  const resendConfirmationEmail = () => {
    if (email) {
      setResendLoading(true);
      Auth.resendSignUp(email);
            setTimeout(() => {
        toast.success("Confirmation email sent successfully");
        setResendLoading(false);
      }, 500);
    }
  };

  const changePasswordAction = useRequest(
    (values) => changePasswordApi(values),
    {
      manual: true,
      onSuccess: () => {
        toast.success("Password successfully updated");
        history.push("/dashboard/login");
      },
    },
  );

  const handleSubmit = (e: any) => {
    e.preventDefault();

    const password = e.target["password"].value;
    const confirmPassword = e.target["confirmPassword"].value;

    if (password !== confirmPassword) {
      toast.warn("Password does not match!");
      return;
    }

    changePasswordAction.run({ username, password });
  };

  useEffect(() => {
    if (!token || !otp) {
      setMessage("Invalid link");
      setConfirmed(false);
      setLoading(false);
      return;
    }

    const payload: any = jwtDecode(token);

    if (!payload?.email || !payload?.username) {
      setMessage("Invalid link");
      setConfirmed(false);
      setLoading(false);
      return;
    }

    setEmail(payload.email);
    setUsername(payload.username);

    confirmAccount(payload.email, otp);

  }, []);

  return (
    <AuthLayout>
      {loading ? (
        <div className="row row-center">
          <LoadingOutlined
            style={{
              color: "#000",
              fontSize: 28,
              marginRight: 14,
              marginTop: 2,
            }}
          />
          <div className="sent-printdata">
            <span className="color-dark">{t("confirmingAccount")}</span>
          </div>
        </div>
      ) : (
        <>
          {confirmed ? (
            <div className="card">
              <div className="text-center mb-20">
                <p style={{ fontSize: 22 }} className="f-w-500">
                  {t("setPassword")}
                </p>
              </div>
              <form className="form" onSubmit={handleSubmit}>
                <div className="form__row">
                  <p>{t("password")}</p>
                  <input
                    name="password"
                    type="password"
                    required
                    placeholder={t("password")}
                  />
                </div>
                <div className="form__row">
                  <p>{t("confirmPassword")}</p>
                  <input
                    name="confirmPassword"
                    type="password"
                    required
                    placeholder={t("confirmPassword")}
                  />
                </div>
                <div className="form__row">
                  <button
                    disabled={changePasswordAction.loading}
                    className="button form-control mt-10"
                    type="submit"
                  >
                    {changePasswordAction.loading ? (
                      <LoadingOutlined />
                    ) : (
                      t("setPassword")
                    )}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <>
              <div className="sent-printdata">
                <XMarkIcon />
                <span className="color-dark">{message}</span>
              </div>
              {email && (
                <button
                  disabled={resendLoading}
                  onClick={() => resendConfirmationEmail()}
                  className="button button-small mt-20"
                >
                  {resendLoading && (
                    <LoadingOutlined style={{ marginRight: 10 }} />
                  )}
                  {t("resendEmail")}
                </button>
              )}
            </>
          )}
        </>
      )}
    </AuthLayout>
  );
};

export default Confirm;
